import { z } from 'zod';
import { currencySchema } from './org';
import { type PriceMatrix, priceMatrixSchema } from './price-matrix';
import {
  type AddonOptions,
  type AddonProduct,
  addonOptionsSchema,
  type Product,
  type ProductOptions,
  productOptionsSchema,
  type ScriptOptions,
  scriptQuoteOptionsSchema,
  type TitleOptions,
} from './products';

/** `org_pricing.discount_percent`: numeric(5,2), 0 to 100. */
export const discountPercentSchema = z
  .number()
  .min(0)
  .max(100)
  .refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-9, 'at most two decimal places');

/** Everything that has a price: a report, or an add-on pack bought for a script project later. */
export type PricedProduct = Product | AddonProduct;
export type PricedOptions = ProductOptions & AddonOptions;

export type PriceInput<P extends PricedProduct = PricedProduct> = {
  product: P;
  options: PricedOptions[P];
  matrix: PriceMatrix;
  /** Null means no org discount. */
  discountPercent: number | null;
};

export type ComputedPrice = { listAmountCents: number; discountCents: number; amountCents: number };

/** A title report is one cell: scope × turnaround. */
function titleListAmount(matrix: PriceMatrix, o: TitleOptions): number {
  return matrix.title[o.scope][`${o.turnaroundDays}`];
}

/**
 * A script report is its base cell (length × turnaround) plus the clearance tier and the version pack. The
 * included five clearances and zero versions cost nothing; the scope is not priced.
 */
function scriptListAmount(matrix: PriceMatrix, o: ScriptOptions): number {
  const base = matrix.script.base[o.length][`${o.turnaroundDays}`];
  const clearances =
    o.additionalClearances === 5
      ? 0
      : matrix.script.additional_clearances[`${o.additionalClearances}`];
  const versions = o.versions === 0 ? 0 : matrix.script.versions[`${o.versions}`];
  return base + clearances + versions;
}

/** The list amount of any priced product. An add-on pack is the same cell the up-front tier is priced from. */
function listAmount(matrix: PriceMatrix, product: PricedProduct, options: unknown): number {
  switch (product) {
    case 'title':
      return titleListAmount(matrix, productOptionsSchema('title').parse(options));
    case 'script':
      return scriptListAmount(matrix, productOptionsSchema('script').parse(options));
    case 'script_additional_clearances': {
      const { quantity } = addonOptionsSchema('script_additional_clearances').parse(options);
      return matrix.script.additional_clearances[`${quantity}`];
    }
    case 'script_versions': {
      const { quantity } = addonOptionsSchema('script_versions').parse(options);
      return matrix.script.versions[`${quantity}`];
    }
  }
}

/**
 * The one pricing function: the product's list amount from the matrix, then the org's percentage on the whole of
 * it, the amount rounded half-up to the minor unit. Integer arithmetic throughout: the percentage becomes basis
 * points, so no float ever holds money. Customers only ever see `amountCents`; the list amount and the discount
 * are for staff and the order snapshot.
 */
export function computePrice<P extends PricedProduct>(input: PriceInput<P>): ComputedPrice {
  const matrix = priceMatrixSchema.parse(input.matrix);
  const listAmountCents = listAmount(matrix, input.product, input.options);
  const bps = Math.round(discountPercentSchema.parse(input.discountPercent ?? 0) * 100);
  // a list amount is a handful of cells ≤ 2^31 and bps ≤ 10^4, so the product stays inside the 2^53 safe range.
  const amountCents = Math.floor((listAmountCents * (10000 - bps) + 5000) / 10000);
  return { listAmountCents, discountCents: listAmountCents - amountCents, amountCents };
}

/**
 * `POST /api/pricing/quote`. A title is priced from its options. A script names its project instead of a length:
 * the worker reads the length from that project's analysis, never from the browser.
 */
export const quoteRequestSchema = z.discriminatedUnion('product', [
  z.strictObject({
    organizationId: z.uuid(),
    product: z.literal('title'),
    options: productOptionsSchema('title'),
  }),
  z.strictObject({
    organizationId: z.uuid(),
    product: z.literal('script'),
    projectId: z.uuid(),
    options: scriptQuoteOptionsSchema,
  }),
]);
export type QuoteRequest = z.infer<typeof quoteRequestSchema>;

/**
 * What a customer may know about a price: the final net amount, or nothing at all when the org's billing amounts
 * are hidden. The list amount and the discount never leave the worker on this path.
 */
export const quoteResponseSchema = z.union([
  z.strictObject({ hidden: z.literal(true) }),
  z.strictObject({ amountCents: z.number().int().nonnegative(), currency: currencySchema }),
]);
export type QuoteResponse = z.infer<typeof quoteResponseSchema>;
